import { useCallback, useEffect, useState } from "react";
import { Button, Nav } from "react-bootstrap";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getClaim, getClaimBadges, setClaimFinished, recheckClaimFinished } from "../services/apiClient.js";
import { formatDateTime, statusAge } from "../utils/date.js";
import ClaimDetailsTab from "../components/claims/ClaimDetailsTab.jsx";
import ClaimStatusTab from "../components/claims/ClaimStatusTab.jsx";
import ClaimProgressTab from "../components/claims/ClaimProgressTab.jsx";
import ClaimInspectionTab from "../components/claims/ClaimInspectionTab.jsx";
import ClaimQuoteTab from "../components/claims/ClaimQuoteTab.jsx";
import ClaimOrdersTab from "../components/claims/ClaimOrdersTab.jsx";
import ClaimPowTab from "../components/claims/ClaimPowTab.jsx";
import ClaimProgressClaimsTab from "../components/claims/ClaimProgressClaimsTab.jsx";
import ClaimFilesTab from "../components/claims/ClaimFilesTab.jsx";
import ClaimInvoicesTab from "../components/claims/ClaimInvoicesTab.jsx";
import ClaimQccTab from "../components/claims/ClaimQccTab.jsx";
import ClaimGlance from "../components/claims/ClaimGlance.jsx";
import {
  FaRegStar, FaStar, FaHistory, FaListUl, FaCamera, FaCalculator,
  FaPaperclip, FaClipboardList, FaCalendarAlt, FaFileContract,
  FaFileInvoiceDollar, FaFileInvoice, FaClipboardCheck,
} from "react-icons/fa";
import { addFavouriteJob, listFavouriteJobs, removeFavouriteJob, isAbortError } from "../services/apiClient.js";
import { announceFavouritesChanged } from "../components/home/FavouriteJobChips.jsx";
import { ClaimBadgesContext } from "../components/claims/claimBadgesContext.js";

// The claim workspace: a left tab rail (medispace project-workspace pattern) and
// a content pane. The active tab can be deep-linked via ?tab= (used by the search
// page's jump icons).
//
// TAB ORDER per Karen (2026-07-25): Status & History · Details · Inspection ·
// Quotes · Files · Progress Notes · POW · Orders · Invoicing · Progress Claims.
// (Landing tab stays Details — see `activeTab` default below.) QCC isn't in her
// list; it keeps its slot at the end and only appears once the job is Accepted /
// Completed / Declined (legacy LogMaster visibility rule).
const TABS = [
  { key: "status", icon: FaHistory, label: "Status & History" },
  { key: "details", icon: FaListUl, label: "Details" },
  { key: "inspection", icon: FaCamera, label: "Inspection" },
  { key: "quotes", icon: FaCalculator, label: "Quotes" },
  { key: "files", icon: FaPaperclip, label: "Files" },
  { key: "progress", icon: FaClipboardList, label: "Progress Notes" },
  { key: "pow", icon: FaCalendarAlt, label: "POW" },
  { key: "orders", icon: FaFileContract, label: "Orders" },
  { key: "invoicing", icon: FaFileInvoiceDollar, label: "Invoicing" },
  { key: "progress-claims", icon: FaFileInvoice, label: "Progress Claims" },
  { key: "qcc", icon: FaClipboardCheck, label: "QCC", statuses: ["A", "C", "D"] },
];
const TAB_KEYS = new Set(TABS.map((t) => t.key));

// Tabs that render their own printed document header (shared/PrintHeader.jsx),
// and therefore do NOT want the generic claim-identity line above it — the
// document's own title already names the job. Add a tab here when you give it a
// PrintHeader.
const TABS_WITH_PRINT_HEADER = new Set(["pow"]);

// FINISHED — a closed job with nothing left outstanding (docs/finished-jobs.md).
//
// Shown only on a closed job, because that is the only place the state means
// anything. Two directions, both open to anyone (KS rule 1): the gate is the
// badge set, not a permission, and the system has already checked that nothing
// is outstanding before a job can finish on its own.
//
// The state is deliberately legible rather than silent plumbing. A finished job
// renders no badges and sinks in the attention sort, and somebody looking at a
// quiet job needs to be able to see WHY it is quiet.
function FinishedControl({ claim, badges, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!claim || !["C", "D"].includes(claim.current_status_code)) return null;

  // A JOB CANNOT BE FINISHED WHILE A BADGE IS OUTSTANDING (KS rule 2). The
  // service refuses it; this greys the control and says WHY rather than letting
  // somebody click into a refusal. `permanent` badges are excluded because they
  // are labels, not tasks — no action clears them, so they must not block.
  //
  // Greyed rather than hidden, deliberately, and it is the one place in this app
  // where that is the better answer: a job that is nearly finished should show
  // that finishing is the next thing, and the tooltip is what turns "you cannot"
  // into "here is what is left".
  const blocking = (badges || []).filter((b) => !b.permanent);

  async function set(value) {
    setBusy(true);
    try {
      await setClaimFinished(claim.id, value);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  }

  if (claim.finished) {
    return (
      <div className="claim-status mt-1">
        <span className="claim-status-pill bg-secondary-subtle">Finished</span>
        {/* Re-opening is the cheap direction and must never be the guarded one:
            a job wrongly un-finished costs somebody a glance, a job wrongly
            finished costs somebody money. */}
        <Button variant="link" size="sm" className="p-0 ms-2 small text-decoration-none"
          disabled={busy} onClick={() => set(false)}>
          re-open
        </Button>
      </div>
    );
  }
  return (
    <div className="claim-status mt-1">
      <Button variant="link" size="sm" className="p-0 small text-decoration-none"
        disabled={busy || blocking.length > 0}
        data-hint={blocking.length
          ? `Still outstanding: ${blocking.map((b) => b.label).join(", ")}. Clear ${blocking.length === 1 ? "it" : "them"} first.`
          : "Nothing is outstanding on this job — close it out."}
        onClick={() => set(true)}>
        mark finished
      </Button>
    </div>
  );
}

// PIN / UNPIN this job (Robert 2026-09-02). ☆ beside the job number in the
// rail: filled = pinned (click unpins), outline = pin it. At the cap of three
// the star stays clickable and the server's message says what to do — a
// disabled control with no explanation is how "it's broken" reports start.
function FavouriteStar({ claimId }) {
  const [favs, setFavs] = useState(null); // null until loaded — render nothing rather than the wrong star
  useEffect(() => {
    const controller = new AbortController();
    listFavouriteJobs({ signal: controller.signal })
      .then(setFavs)
      .catch((err) => { if (!isAbortError(err)) setFavs([]); });
    return () => controller.abort();
  }, [claimId]);

  if (!favs) return null;
  const mine = favs.some((f) => Number(f.claim_id) === Number(claimId));
  const Icon = mine ? FaStar : FaRegStar;
  return (
    <Icon
      role="button"
      size={14}
      className={mine ? "text-warning" : "text-muted"}
      data-hint={mine
        ? "Remove this job from your favourites"
        : "Add to your favourites — the job shows as a chip in the header for one-click return (max 3)"} aria-label={mine
        ? "Remove this job from your favourites"
        : "Add to your favourites — the job shows as a chip in the header for one-click return (max 3)"}
      onClick={async () => {
        try {
          const list = mine ? await removeFavouriteJob(claimId) : await addFavouriteJob(claimId);
          setFavs(list);
          announceFavouritesChanged(list);
        } catch (e) {
          window.alert(e.response?.data?.error || "Unable to change favourites.");
        }
      }}
    />
  );
}

function ClaimWorkspace() {
  // The URL carries the CLAIM NUMBER (Robert 2026-08-20) — the number staff
  // read and say — never the internal row id, which differs between databases
  // imported separately. Old id URLs (bookmarks, history) still resolve (the
  // claim GET accepts either) and are redirected to the number form below.
  // Everything AFTER the load uses summary.id: the backend's other routes are
  // id-only, so the raw URL value must never reach a child API call.
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [badges, setBadges] = useState([]);
  const claimId = summary?.id ?? null;

  const requested = searchParams.get("tab");
  const activeTab = TAB_KEYS.has(requested) ? requested : "details";

  function selectTab(key) {
    if (key) setSearchParams({ tab: key }, { replace: true });
  }

  const loadSummary = useCallback(() => {
    getClaim(urlId)
      .then((data) => {
        setSummary(data.claim);
        setLoadError("");
      })
      .catch((err) => {
        setSummary(null);
        setLoadError(err.response?.status === 404 ? "No job with that number." : "Unable to load the job.");
      });
  }, [urlId]);

  // Navigating to a DIFFERENT job: drop the old identity before fetching, so
  // the previous job can never leak into the new URL's first paint (and the
  // redirect below can never act on a stale summary).
  useEffect(() => {
    setSummary(null);
    setBadges([]);
    setLoadError("");
    loadSummary();
  }, [loadSummary]);

  // A legacy id URL redirects to the number form — only when the loaded
  // claim's ID is what the URL holds, which is the one unambiguous signal
  // (a stale summary from the previous job can never trip it).
  useEffect(() => {
    if (summary && String(summary.id) === String(urlId) && String(summary.claim_number) !== String(urlId)) {
      navigate(`/claims/${summary.claim_number}${location.search}`, { replace: true });
    }
  }, [summary, urlId, navigate, location.search]);

  // The browser tab wears the job's identity (Medispace-style, the parked
  // wish): "#76481 Quotes — ClaimsDesk 2.0".
  const activeTabLabel = TABS.find((t) => t.key === activeTab)?.label || "";
  useEffect(() => {
    if (!summary?.claim_number) return undefined;
    document.title = `#${summary.claim_number} ${activeTabLabel} — ClaimsDesk Archive`;
    return () => {
      document.title = "ClaimsDesk Archive";
    };
  }, [summary?.claim_number, activeTabLabel]);

  // This claim's Needs-Attention badges, shared with every tab's TabHeader via
  // context (the chips render inline in the header line).
  //
  // Re-fetchable, not fetched once: the chips say what the job still needs, so
  // doing the thing has to remove them. Karen Renall 2026-08-05, "I've done a
  // couple of the overdue items on 75739 but they don't disappear as soon as
  // done" — they were right, and only a page reload cleared them.
  const loadBadges = useCallback(() => {
    if (!claimId) return;
    getClaimBadges([claimId])
      .then((map) => setBadges(map[claimId] || []))
      .catch(() => setBadges([]));
  }, [claimId]);

  useEffect(() => {
    loadBadges();
  }, [loadBadges]);

  // What a tab calls when it changes something. Both the header facts AND the
  // chips are derived from the claim, so refreshing one without the other
  // leaves the page disagreeing with itself.
  const handleChanged = useCallback(() => {
    // A finished job that has just GAINED work comes back into view immediately.
    // One way only — nothing here ever finishes a job, that stays a person's
    // decision. It matters because there is no scheduler on live to catch a
    // closed job going bad later (docs/finished-jobs.md).
    if (!claimId) return;
    recheckClaimFinished(claimId)
      .catch(() => {})
      .finally(() => {
        loadSummary();
        loadBadges();
      });
  }, [claimId, loadSummary, loadBadges]);

  // Everything below needs the resolved claim — tabs receive summary.id, so
  // they cannot render (and fire id-based API calls) until it exists.
  if (!summary) {
    return (
      <div className="p-4 text-muted">
        {loadError ? (
          <>
            <div className="text-danger mb-2">{loadError}</div>
            <Button size="sm" variant="outline-secondary" onClick={loadSummary}>
              Try again
            </Button>
          </>
        ) : (
          "Loading job…"
        )}
      </div>
    );
  }

  return (
    <ClaimBadgesContext.Provider
      value={{ claimId, claimNumber: summary.claim_number, badges, refreshBadges: loadBadges }}
    >
    <div>
      {/* No full-width header row — vertical space is premium. The claim identity
          lives at the top of the tab rail; everything else is one click away in
          the ⓘ "Job at a glance" panel (the Details tab stays the full record). */}
      {/* The claim number, for PAPER only. On screen it lives at the top of the
          tab rail, but the rail does not print and a printed job sheet with no
          job number on it is not much use to a contractor — Karen Renall,
          2026-08-11: "it doesn't show the side bar but still needs to show
          job # maybe in front of Claim Details".
          SUPPRESSED on a tab that prints its own document header, which already
          names the job in its title — two identity lines stacked above a
          letterhead is worse than either alone (Robert 2026-08-11). */}
      {TABS_WITH_PRINT_HEADER.has(activeTab) ? null : (
        <div className="d-none d-print-block mb-2">
          <h5 className="mb-0">#{summary ? summary.claim_number : ""}
            {summary?.client_name ? ` · ${summary.client_name}` : ""}</h5>
        </div>
      )}
      <div className="d-flex">
        {/* NO-PRINT. This rail is navigation, and navigation on paper is just a
            column of dead words down the left of every printed job sheet —
            reported twice on 2026-08-11 (POW and Claim Details) and true in
            every browser, not a Chrome/Firefox difference: nothing here ever
            carried a print class. */}
        <div className="claim-rail border-end pe-2 me-3 d-print-none" style={{ width: 158, flex: "0 0 auto" }}>
          <div className="claim-rail-summary mb-2 pb-2 border-bottom">
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0 text-nowrap">#{summary ? summary.claim_number : "…"}</h5>
              <ClaimGlance claim={summary} />
              {summary ? <FavouriteStar claimId={claimId} /> : null}
            </div>
            {summary ? (
              <>
                <div className="small text-muted text-truncate" data-hint={summary.client_name || undefined}>
                  {summary.client_name || "—"}
                </div>
                {/* The rail's answer to "where is this job". Two changes from the
                    grey pill it replaces (Karen Sweatman / Robert 2026-08-12).

                    It no longer uses bg="secondary": Bootswatch treats $secondary
                    as a shade of the PAGE in several themes — Zephyr's is #fff —
                    which is the same trap that printed the Files button white on
                    white. Border + emphasis colour are stable in all fifteen.

                    And it says WHEN, not just what. "Completed" alone is a label;
                    how long a job has sat where it is, is the question the office
                    actually asks, and it is the whole basis of the RDR and File
                    Life. Deliberately NOT colour-coded by stage: no such colour
                    language exists yet (the job list renders status as plain
                    text), and inventing one here would make the two disagree. */}
                {/* A job ACCEPTED and then re-requested by a new inspection keeps
                    Accepted as its headline and gains a second line (Karen
                    Sweatman's own sketch, 13 Aug). She had asked for a status
                    RULE — once accepted, always accepted — which cannot be done:
                    from A there is no legal move to I or Q, so the re-inspection
                    could never be recorded (docs/TODO.md, 13 Aug meeting). What
                    she actually needed was for the rail to stop reading as though
                    the job had fallen back to square one, and that is a display.
                    The engine still moves the job to R; nothing here changes it.

                    Accepted stays the headline because that is the job's standing
                    and "38 days since accepted" is the number the office chases
                    on — the re-inspection is an interruption to an accepted job,
                    not a new job. */}
                {summary.re_request ? (
                  <>
                    <div className="claim-status mt-1">
                      <span className="claim-status-pill">Accepted</span>
                      <span
                        className="claim-status-since"
                        data-hint={formatDateTime(summary.re_request.accepted_at)}
                      >
                        {statusAge(summary.re_request.accepted_at)}
                      </span>
                    </div>
                    <div className="claim-status mt-1">
                      <span className="claim-status-pill">Re-Requested</span>
                      <span
                        className="claim-status-since"
                        data-hint={formatDateTime(summary.re_request.re_requested_at)}
                      >
                        {statusAge(summary.re_request.re_requested_at)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="claim-status mt-1">
                    <span className="claim-status-pill">{summary.status_name}</span>
                    {/* Only while the job is still MOVING. On a finished job the
                        number answers a question nobody asked — 15 days is not how
                        long it has been waiting, it is how long ago it was done —
                        so `is_terminal` gates it rather than a hardcoded 'C'. */}
                    {summary.status_since && !summary.status_is_terminal ? (
                      <span className="claim-status-since" data-hint={formatDateTime(summary.status_since)}>
                        {statusAge(summary.status_since)}
                      </span>
                    ) : null}
                  </div>
                )}
                {/* Who the job is for and what kind it is — the two facts the
                    office kept having to open Details for (Mia 2026-09-10: "we
                    could have easily sent this one as a quote, but it's a do and
                    charge"). Under the status, where the rail had the room. */}
                {summary.insurer_company_name || summary.job_type_name ? (
                  <div
                    className="small text-muted text-truncate mt-1"
                    data-hint={[summary.insurer_company_name, summary.job_type_name].filter(Boolean).join(" · ")}
                  >
                    {[summary.insurer_company_name, summary.job_type_name].filter(Boolean).join(" · ")}
                  </div>
                ) : null}
                <FinishedControl claim={summary} badges={badges} onChanged={handleChanged} />
              </>
            ) : null}
          </div>
          <Nav
            variant="pills"
            activeKey={activeTab}
            onSelect={selectTab}
            className="flex-column"
          >
            {TABS.filter(
              (tab) => !tab.statuses || tab.statuses.includes(summary?.current_status_code)
            ).map((tab) => (
              <Nav.Item key={tab.key}>
                <Nav.Link eventKey={tab.key}>
                  <tab.icon aria-hidden="true" /><span>{tab.label}</span>
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>

        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {activeTab === "details" && (
            <ClaimDetailsTab claimId={claimId} onChanged={handleChanged} />
          )}
          {activeTab === "status" && (
            <ClaimStatusTab claimId={claimId} onChanged={handleChanged} />
          )}
          {activeTab === "inspection" && (
            <ClaimInspectionTab claimId={claimId} onChanged={handleChanged} />
          )}
          {activeTab === "progress" && <ClaimProgressTab claimId={claimId} onChanged={handleChanged} />}
          {activeTab === "quotes" && <ClaimQuoteTab claimId={claimId} onChanged={handleChanged} />}
          {activeTab === "orders" && <ClaimOrdersTab claimId={claimId} onChanged={handleChanged} />}
          {/* `summary` only for the printed header's default title — the tab
              fetches everything else it needs itself. */}
          {activeTab === "pow" && <ClaimPowTab claimId={claimId} claim={summary} />}
          {activeTab === "progress-claims" && <ClaimProgressClaimsTab claimId={claimId} onChanged={handleChanged} />}
          {activeTab === "files" && <ClaimFilesTab claimId={claimId} />}
          {activeTab === "invoicing" && <ClaimInvoicesTab claimId={claimId} />}
          {activeTab === "qcc" && <ClaimQccTab claimId={claimId} onChanged={handleChanged} />}
          {!["details", "status", "inspection", "progress", "quotes", "orders", "pow", "progress-claims", "files", "invoicing", "qcc"].includes(activeTab) ? (
            <div className="text-muted">
              “{TABS.find((t) => t.key === activeTab)?.label}” — coming soon.
            </div>
          ) : null}
        </div>
      </div>
    </div>
    </ClaimBadgesContext.Provider>
  );
}

export default ClaimWorkspace;
